// Service for Monthly Cuts — Envío de conceptos a remuneraciones (ASIS-4971 / FE-012)
import { apiClient, API_CONFIG } from '@/api/core';
import type { ApiResponse } from '@/api/shared';
import type {
  LC_SendMonthlyCutsToPayrollIn,
  LC_SubmitPayrollSubmissionJobOut,
  LC_GetPayrollSubmissionResultOut,
  LC_GetPayrollSubmissionAvailabilityOut,
  SendMonthlyCutsToPayrollInput,
} from '../types';


export class MonthlyCutsSendToPayrollService {
  // POST /v1/monthlycuts/payroll-submissions
  // Encola el envío de los cortes seleccionados al sistema de remuneraciones. Responde 202 con el
  // jobId del proceso asíncrono; el resultado por corte se obtiene sondeando getResult(jobId).
  // Devuelve 404 NOT_FOUND_PAYROLL_INTEGRATION si la empresa no tiene integración activa y
  // NOT_FOUND_MONTHLY_CUTS_FOR_PAYROLL si ningún corte es válido.
  async send(
    input: SendMonthlyCutsToPayrollInput,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_SubmitPayrollSubmissionJobOut>> {
    const body: LC_SendMonthlyCutsToPayrollIn = {
      monthlyCutIds: input.monthlyCutIds,
      // 'REX' es el valor por defecto del backend: solo se envía targetSystem cuando difiere.
      ...(input.targetSystem && input.targetSystem !== 'REX'
        ? { targetSystem: input.targetSystem }
        : {}),
    };

    return apiClient.post<LC_SubmitPayrollSubmissionJobOut>(
      API_CONFIG.ENDPOINTS.MONTHLY_CUTS_PAYROLL_SUBMISSIONS,
      body,
      { signal },
    );
  }

  // GET /v1/monthlycuts/payroll-submissions/{jobId}
  // Devuelve el estado del job de envío. Cuando status === 'Completado', `result` trae el resultado
  // por corte (mismo shape que el envío síncrono previo).
  async getResult(
    jobId: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_GetPayrollSubmissionResultOut>> {
    return apiClient.get<LC_GetPayrollSubmissionResultOut>(
      API_CONFIG.ENDPOINTS.MONTHLY_CUTS_PAYROLL_SUBMISSION_RESULT(jobId),
      { signal },
    );
  }

  // GET /v1/monthlycuts/payroll-submissions/availability
  // Indica si la empresa autenticada tiene una integración de remuneraciones activa.
  async getAvailability(
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_GetPayrollSubmissionAvailabilityOut>> {
    return apiClient.get<LC_GetPayrollSubmissionAvailabilityOut>(
      API_CONFIG.ENDPOINTS.MONTHLY_CUTS_PAYROLL_AVAILABILITY,
      { signal },
    );
  }
}

export const monthlyCutsSendToPayrollService = new MonthlyCutsSendToPayrollService();
